import React, { useContext, useEffect, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import {
  FormulasV2,
  isOfferSuccessfull,
  LouiseOffer,
  Warning,
} from '../../../../../types/Quotes';
import { IconActionClose } from 'wg-fe-ui';
import { useTranslation } from 'react-i18next';
import CollapseContainer from '../CollapseContainer';
import OfferGeneralInfo from './OfferGeneralInfo';
import OfferOptionContainer from './offer-options/OfferOptionContainer';
import OfferCoveragesContainer from './OfferCoveragesContainer';
import OfferDocuments from './OfferDocuments';
import BasicModal from '../../../../common/src/components/BasicModal';
import { getCoveragesGroups } from '../../services/productFactoryService';
import SingleOfferHeaderMobile from './SingleOfferHeaderMobile';
import OfferFormulas from './offer-options/OfferFormulas';
import { OfferState } from '../../services/offersService';
import OfferBenefit from './offer-options/OfferBenefit';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  offer: LouiseOffer;
  showModal: boolean;
  onClose: () => void;
  onOptionsChange: (arg0: string[]) => void;
}

const SingleOfferDetailModal: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
  showModal,
  onClose,
  onOptionsChange,
}) => {
  const theme = useContext(ThemeContext);
  const { t } = useTranslation();
  const [goodCoverages, badCoverages] = getCoveragesGroups(offer.quality);

  const documents = [
    {
      label: t('TERMS'),
      url: offer.info?.terms || '',
    },
    {
      label: t('IPID'),
      url: offer.info?.ipid || '',
    },
  ];

  const [formulas, setFormulas] = useState<FormulasV2>();
  const [warnings, setWarnings] = useState<Warning[]>();

  useEffect(() => {
    if (offer.state === OfferState.FETCHED && isOfferSuccessfull(offer)) {
      // Sets the initial values if the quote was ever retrieved
      setFormulas(offer.details.formulas_v2);
      setWarnings(offer.details.warnings);
    }
  }, [offer]);

  return (
    <StyledBasicModal showModal={showModal} onClose={onClose}>
      <CloseButton onClick={onClose}>
        <IconActionClose color={theme.labels.guaranteeText} />
      </CloseButton>
      <SingleOfferHeaderMobile offer={offer} />
      <Content>
        <CollapseContainer defaultOpen={true} title={t('General information')}>
          <OfferGeneralInfo offer={offer} />
        </CollapseContainer>
        <CollapseContainer
          title={t('SALES-CONVERSATION_OFFERS_SINGLE-OFFER_OPTIONS')}
        >
          <Text>{t('Tap the options to enable / disable them')}</Text>
          <OfferOptionContainer
            offer={offer}
            onOptionsChange={onOptionsChange}
          />
        </CollapseContainer>
        {formulas && (
          <CollapseContainer title={t('Formulas')}>
            <OfferFormulas offer={offer} formulas={formulas} />
          </CollapseContainer>
        )}

        {warnings && warnings.length > 0 && (
          <CollapseContainer title={t('BENEFITS')}>
            <OfferBenefit benefits={warnings} withTitle={false} />
          </CollapseContainer>
        )}

        {goodCoverages && goodCoverages.length > 0 && (
          <CollapseContainer title={t('Important coverage')}>
            <Text>{t('Some of the benefits that are important to you')}</Text>
            <OfferCoveragesContainer coverages={goodCoverages} />
          </CollapseContainer>
        )}
        {badCoverages && badCoverages.length > 0 && (
          <CollapseContainer title={t('Less covered')}>
            <Text>{t('What is less covered according to your needs')}</Text>
            <OfferCoveragesContainer coverages={badCoverages} />
          </CollapseContainer>
        )}
        {!isSocialLoginUrl && (
          <CollapseContainer title={t('Documents')}>
            <Text>{t('All documents related to this offer')}</Text>
            <OfferDocuments documents={documents} />
          </CollapseContainer>
        )}
      </Content>
    </StyledBasicModal>
  );
};

const StyledBasicModal = styled(BasicModal)`
  > div:last-child {
    width: 100%;
    margin: 0.8rem;
  }
`;

const Text = styled.p`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.labels.guaranteeText};
  margin-bottom: 1.6rem;
`;

const Content = styled.div`
  > div {
    border-top: 1px solid ${({ theme }) => theme.gray[50]};
    padding: 0 1.6rem;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 1.6rem;
  right: 1.6rem;
  border-radius: 50px;
  width: 3.2rem;
  height: 3.2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.gray[50]};

  &:active {
    background-color: ${({ theme }) => theme.gray[100]};
  }

  > svg {
    width: 3rem;
    height: 3rem;
  }
`;

export default SingleOfferDetailModal;
